'use client'

import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { createContext, useContext, useEffect, useReducer } from 'react'
import { useSession } from 'session/src/hooks/useSession'

import type { AllUserInfo } from '../types/user.types'
import { INITIAL_ALL_USER_INFO, PossibleInfo } from '../utils/constants'
import { getActivePurchases } from '../utils/getActivePurchases/getActivePurchases'
import { getInactivePurchases } from '../utils/getInactivePurchases/getInactivePurchases'
import { myAccountReducer } from '../utils/myAccountReducer/myAccountReducer'
import { userPurchases } from '../utils/userPurchases/userPurchases'

interface MyAccountProviderProps {
	children: React.ReactNode
}

const MyAccountContext = createContext<AllUserInfo>(INITIAL_ALL_USER_INFO)

function MyAccountProvider({
	children,
}: Readonly<MyAccountProviderProps>): React.ReactNode {
	const { token, isReady, logged } = useSession()
	const { country, channel } = useMasterData()

	const [userOrders, addUserOrder] = useReducer(
		myAccountReducer,
		INITIAL_ALL_USER_INFO
	)

	useEffect(() => {
		if (!isReady || !logged) {
			return
		}
		const getOrders = async ({
			purchaseType,
		}: {
			purchaseType: PossibleInfo
		}) => {
			const purchase =
				purchaseType === PossibleInfo.ACTIVE_PURCHASE
					? await getActivePurchases({
							token,
							channel,
							languageISO: country.languageISO,
							countryISO: country.countryISO,
						})
					: await getInactivePurchases({
							token,
							channel,
							languageISO: country.languageISO,
							countryISO: country.countryISO,
						})
			if (purchase?.action !== undefined && purchase?.action.length > 0) {
				const newUserAction = userPurchases[purchase.actionType].adaptFunction(
					purchase.action
				)
				addUserOrder({
					userInfo: newUserAction,
					userInfoType: purchase.actionType,
				})
			}
		}
		getOrders({ purchaseType: PossibleInfo.ACTIVE_PURCHASE })
		getOrders({ purchaseType: PossibleInfo.INACTIVE_PURCHASE })
	}, [isReady, token])

	return userOrders.userInfo ? (
		<MyAccountContext.Provider value={userOrders}>
			{children}
		</MyAccountContext.Provider>
	) : null
}

const useMyAccount = () => {
	const context = useContext(MyAccountContext)
	if (context === undefined) {
		throw new Error('useMyAccount must be used within a MyAccountProvider')
	}
	return context
}

export { MyAccountProvider, MyAccountContext, useMyAccount }
